import mixpanel from 'mixpanel-browser';

import { MIXPANEL_REPLAYS_TOKEN } from '@constants/config';
import { BASKET_ID_KEY } from '@constants/storageKeys';

// The SDK builds every evaluation context as
// `{distinct_id, device_id, ...configuredContext}`, reading distinct_id and
// device_id from its own persistence at call time. Whatever we configure wins,
// so `basket_id` is the only key we may set: passing distinct_id ourselves
// would pin it to a stale value and detach flag evaluation from `identify()`.
interface MixpanelFlagsContext {
  basket_id?: string;
}

// Group Analytics key. The flags context only decides *assignment*; for
// Experiments to count exposures and metrics per basket, the events themselves
// must carry the key, so it is also registered as a super property.
const BASKET_GROUP_KEY = 'basket_id';

// `getInitialFlagsContext` runs while `tracker.ts` is still evaluating, and
// `tracker.startSession()` — which is what ultimately triggers the adapter's
// `mixpanel.identify()` — sits at the bottom of that module. A throw here
// would take the whole module down with it and leave the user unidentified,
// so storage access must never be allowed to escape. Session storage does
// throw in partitioned/blocked-storage contexts, the partner-app webview
// included.
const readStoredBasketId = () => {
  try {
    return window.sessionStorage.getItem(BASKET_ID_KEY) || undefined;
  } catch {
    return undefined;
  }
};

const buildFlagsContext = (basketId?: string): MixpanelFlagsContext =>
  basketId ? { [BASKET_GROUP_KEY]: basketId } : {};

// Super properties live in Mixpanel's localStorage persistence, so they
// outlive the session-scoped basket: a basket from a previous visit has to be
// actively unregistered, not merely left unset.
const applyBasketEventProperty = (basketId?: string) => {
  if (basketId) {
    mixpanel.register({ [BASKET_GROUP_KEY]: basketId });
  } else {
    mixpanel.unregister(BASKET_GROUP_KEY);
  }
};

// Mirrors the basket the SDK currently holds so we only pay for a re-fetch
// when it really changed. Seeded by `getInitialFlagsContext`.
let appliedBasketId: string | undefined;

let contextVersion = 0;
const listeners = new Set<() => void>();

const getFlagsContextVersion = () => contextVersion;

const subscribeToFlagsContext = (onChange: () => void) => {
  listeners.add(onChange);
  return () => {
    listeners.delete(onChange);
  };
};

/**
 * Initial `flags.context` for `mixpanel.init`. Seeding the basket here means a
 * reload mid-basket evaluates against the right group on the very first fetch,
 * rather than evaluating without it and re-fetching once React has mounted.
 */
const getInitialFlagsContext = (): MixpanelFlagsContext => {
  appliedBasketId = readStoredBasketId();
  return buildFlagsContext(appliedBasketId);
};

/**
 * Aligns the `basket_id` super property with the seeded basket. Must run after
 * `mixpanel.init`, which `getInitialFlagsContext` precedes.
 */
const applyInitialBasketEventProperty = () => {
  applyBasketEventProperty(appliedBasketId);
};

/**
 * Re-evaluates every flag against `basketId`. Resolves once the SDK has
 * installed the new variants, and only then notifies subscribers: a sync read
 * before that would still hand components the previous basket's variant.
 */
const syncMixpanelFlagsContext = async (basketId: Nullable<string>) => {
  if (!MIXPANEL_REPLAYS_TOKEN) return;

  const nextBasketId = basketId || undefined;
  if (nextBasketId === appliedBasketId) return;

  appliedBasketId = nextBasketId;

  // Before the re-fetch, so the exposure it re-arms is already attributed to
  // the new basket.
  applyBasketEventProperty(nextBasketId);

  // `replace: true` because the SDK merges with an `_.extend` that skips
  // undefined values, so a merging update could never *remove* basket_id when
  // the basket is cleared. We own the whole configured context, so replacing
  // it is safe — distinct_id and device_id are added by the SDK regardless.
  await mixpanel.flags.update_context(buildFlagsContext(nextBasketId), {
    replace: true,
  });

  // A newer sync overtook us while we were awaiting; let it do the announcing
  // so subscribers never re-read for a basket that is already superseded.
  if (appliedBasketId !== nextBasketId) return;

  contextVersion += 1;
  listeners.forEach((listener) => listener());
};

export {
  applyInitialBasketEventProperty,
  getFlagsContextVersion,
  getInitialFlagsContext,
  subscribeToFlagsContext,
  syncMixpanelFlagsContext,
};
export type { MixpanelFlagsContext };
